/**
 * MeasurementTool - A module for measuring distances and areas on an OpenLayers map
 */
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';
import Draw from 'ol/interaction/Draw';
import { transform } from 'ol/proj';
import { getDistance } from 'ol/sphere';
import Polygon from 'ol/geom/Polygon';

export default class MeasurementTool {
  /**
   * @param {import('ol/Map').default} map - The OpenLayers map instance
   * @param {Object} options - Configuration options
   */
  constructor(map, options = {}) {
    this.map = map;
    this.view = map.getView();
    this.options = {
      strokeColor: options.strokeColor || '#0057a8',
      fillColor: options.fillColor || 'rgba(0, 87, 168, 0.15)',
      tooltipClass: options.tooltipClass || 'measure-tooltip',
      ...options
    };

    this.draw = null;
    this.sketch = null;
    this.currentMeasureType = null;
    this.measureSource = new VectorSource();
    this.measureLayer = null;
    this.tooltipElement = null;
    this.controlElement = null;

    this._initialize();
  }

  _initialize() {
    this._createVectorLayer();
    this._createTooltip();
    this._bindEvents();
  }

  _createVectorLayer() {
    this.measureLayer = new VectorLayer({
      source: this.measureSource,
      style: new Style({
        fill: new Fill({ color: this.options.fillColor }),
        stroke: new Stroke({ color: this.options.strokeColor, width: 2 }),
        image: new CircleStyle({
          radius: 5,
          fill: new Fill({ color: this.options.strokeColor })
        })
      })
    });
    this.map.addLayer(this.measureLayer);
  }

  _createTooltip() {
    this.tooltipElement = document.getElementById(this.options.tooltipClass);
    if (!this.tooltipElement) {
      this.tooltipElement = document.createElement('div');
      this.tooltipElement.id = this.options.tooltipClass;
      this.tooltipElement.className = this.options.tooltipClass;
      document.getElementById('map').appendChild(this.tooltipElement);
    }
  }

  _bindEvents() {
    this.controlElement = document.getElementById('measure-control');
    const btnDistance = document.getElementById('btn-distance');
    const btnArea = document.getElementById('btn-area');

    if (this.controlElement) {
      this.controlElement.addEventListener('click', (e) => this._onControlClick(e));
    }

    if (btnDistance) {
      btnDistance.addEventListener('click', (e) => {
        e.stopPropagation();
        this.activate('LineString');
      });
    }

    if (btnArea) {
      btnArea.addEventListener('click', (e) => {
        e.stopPropagation();
        this.activate('Polygon');
      });
    }

    document.addEventListener('click', () => {
      this._hideDropdown();
    });

    // Delete measurement when clicking outside
    this.map.on('singleclick', (evt) => this._onMapClick(evt));
  }

  _onControlClick(e) {
    if (e.target.tagName === 'BUTTON') return;

    if (this.currentMeasureType) {
      this.deactivate();
    } else {
      this.controlElement.classList.add('active');
      this.controlElement.querySelector('.dropdown').style.display = 'block';
    }
    e.stopPropagation();
  }

  _hideDropdown() {
    if (this.controlElement) {
      this.controlElement.classList.remove('active');
      this.controlElement.querySelector('.dropdown').style.display = 'none';
    }
  }

  _onMapClick(evt) {
    if (this.currentMeasureType) return;


    const onMeasurement = this.map.hasFeatureAtPixel(evt.pixel, {
       layerFilter: (layer) => layer === this.measureLayer
    });

  if (!onMeasurement && this.measureSource.getFeatures().length > 0) {


      this.clear();
    }
  }

  /**
   * Activate measurement tool for a specific type
   * @param {string} type - 'LineString' or 'Polygon'
   */
  activate(type) {
    this.currentMeasureType = type;
    this._hideDropdown();
    this._clearTooltip();
    this.measureSource.clear();
    this._addInteraction(type);
  }

  /**
   * Deactivate the measurement tool
   */
  deactivate() {
    this._hideDropdown();
    this._removeInteraction();
    this._clearTooltip();
    this.currentMeasureType = null;
  }

  /**
   * Clear the current measurement
   */
  clear() {
    this.measureSource.clear();
    this._clearTooltip();
  }

  _clearTooltip() {
    if (this.tooltipElement) {
      this.tooltipElement.innerHTML = '';
      this.tooltipElement.style.display = 'none';
    }
  }

  _removeInteraction() {
    if (this.draw) {
      this.map.removeInteraction(this.draw);
      this.draw = null;
    }
  }

  _addInteraction(type) {
    this._removeInteraction();

    this.draw = new Draw({
      source: this.measureSource,
      type: type,
      style: new Style({
        fill: new Fill({ color: this.options.fillColor }),
        stroke: new Stroke({ color: this.options.strokeColor, width: 2 }),
        image: new CircleStyle({
          radius: 5,
          fill: new Fill({ color: this.options.strokeColor })
        })
      })
    });

    this._bindDrawEvents(type);
    this.map.addInteraction(this.draw);
  }

  _bindDrawEvents(type) {
    this.draw.on('drawstart', (evt) => {
      this.sketch = evt.feature;
    });

    this.draw.on('drawend', (evt) => {
      const geometry = evt.feature.getGeometry();
      const measureText = this._calculateMeasurement(geometry, type);
      this.tooltipElement.innerHTML = measureText;
      this.tooltipElement.style.display = 'block';

      this._removeInteraction();
      this._hideDropdown();
      this.currentMeasureType = null;
      this.sketch = null;
    });
  }

  _calculateMeasurement(geometry, type) {
    if (type === 'LineString') {
      return this._formatDistance(this._calculateLength(geometry));
    } else if (type === 'Polygon') {
      return this._formatArea(this._calculateArea(geometry));
    }
    return '';
  }

  _calculateLength(line) {
    const coordinates = line.getCoordinates();
    let length = 0;
    const sourceProj = this.view.getProjection();

    for (let i = 0; i < coordinates.length - 1; i++) {
      const c1 = transform(coordinates[i], sourceProj, 'EPSG:4326');
      const c2 = transform(coordinates[i + 1], sourceProj, 'EPSG:4326');
      length += getDistance(c1, c2);
    }
    return length;
  }

  _calculateArea(polygon) {
    const coords = polygon.getCoordinates()[0];
    if (coords.length < 4) {
      return 0;
    }

    const sourceProj = this.view.getProjection();
    const ring = [];

    for (let i = 0; i < coords.length - 1; i++) {
      ring.push(transform(coords[i], sourceProj, 'EPSG:4326'));
    }

    if (ring.length < 3) {
      return 0;
    }

    const geom = new Polygon([ring]);
    geom.transform('EPSG:4326', 'EPSG:3857');
    return Math.abs(geom.getArea());
  }

  _formatDistance(meters) {
    if (meters >= 1000) {
      return (meters / 1000).toFixed(2) + ' km';
    }
    return meters.toFixed(0) + ' m';
  }

  _formatArea(sqMeters) {
    return (sqMeters / 10000).toFixed(2) + ' hectares';
  }

  /**
   * Destroy the measurement tool and clean up
   */
  destroy() {
    this.deactivate();
    this.clear();
    if (this.measureLayer) {
      this.map.removeLayer(this.measureLayer);
    }
  }
}
